import React, { useState, useEffect } from 'react';
import { MapPin, X, Calendar, Trophy, Clock } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { formatDateForDisplay } from '@/lib/utils/date';

interface MemberMatchesModalProps {
  isOpen: boolean;
  onClose: () => void;
  memberId: string;
  memberName: string;
}

interface Match {
  id: string;
  date: string;
  time: string;
  sport: string;
  venue: {
    name: string;
    city: string;
  };
  opponent: {
    id: string;
    name: string;
    image: string;
  };
  result?: 'win' | 'loss' | 'draw';
}

const MemberMatchesModal: React.FC<MemberMatchesModalProps> = ({
  isOpen,
  onClose,
  memberId,
  memberName
}) => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const defaultAvatar = 'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png';

  useEffect(() => {
    const fetchMatches = async () => {
      if (!memberId) return;
      
      try {
        setIsLoading(true);
        setError(null);

        // Get user's database ID
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('id')
          .eq('auth_id', memberId)
          .single();

        if (userError) throw userError;
        if (!userData) throw new Error('User not found');

        const today = new Date().toISOString().split('T')[0];

        // Fetch past confirmed activities
        const { data: activitiesData, error: activitiesError } = await supabase
          .from('activities')
          .select(`
            id,
            date,
            time,
            sport,
            venue_name,
            venue_city,
            player_1_id,
            player_2_id,
            player_1:player_1_id(id, auth_id, full_name, avatar_url),
            player_2:player_2_id(id, auth_id, full_name, avatar_url)
          `)
          .or(`player_1_id.eq.${userData.id},player_2_id.eq.${userData.id}`)
          .eq('status', 'confirmed')
          .lt('date', today)
          .order('date', { ascending: false });

        if (activitiesError) throw activitiesError;

        // Transform activities to matches
        const transformedMatches = activitiesData.map(activity => {
          // Determine if the member is player 1 or player 2
          const isMemberPlayer1 = activity.player_1_id === userData.id;
          const opponent = isMemberPlayer1 ? activity.player_2 : activity.player_1;

          return {
            id: activity.id,
            date: activity.date,
            time: activity.time,
            sport: activity.sport,
            venue: {
              name: activity.venue_name || 'Unknown venue',
              city: activity.venue_city?.split(',')[0] || 'Unknown location'
            },
            opponent: {
              id: opponent.auth_id,
              name: opponent.full_name,
              image: opponent.avatar_url || defaultAvatar
            }
          };
        });

        setMatches(transformedMatches);
      } catch (error) {
        console.error('Error fetching matches:', error);
        setError(error instanceof Error ? error.message : 'Failed to load matches');
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchMatches();
    }
  }, [memberId, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-dark-50/80 backdrop-blur-sm">
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="bg-dark-200 rounded-2xl w-full max-w-2xl border border-dark-300">
            {/* Header */}
            <div className="p-6 border-b border-dark-300">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">
                    Matches History
                  </h2>
                  <p className="text-dark-800">
                    {memberName}'s past matches
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="text-dark-800 hover:text-white transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <div className="h-8 w-8 border-2 border-gold-400 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-400">
                  <p>{error}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="text-sm underline mt-2 hover:text-red-300"
                  >
                    Try Again
                  </button>
                </div>
              ) : matches.length === 0 ? (
                <div className="text-center py-8">
                  <Trophy className="h-12 w-12 text-dark-800 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">No Matches Found</h3>
                  <p className="text-dark-800">
                    {memberName} hasn't played any matches yet.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {matches.map((match) => (
                    <div
                      key={match.id}
                      className="bg-dark-300 rounded-xl p-4 hover:bg-dark-400 transition-colors"
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <img
                            src={match.opponent.image}
                            alt={match.opponent.name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-dark-400"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = defaultAvatar;
                            }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div>
                              <div className="text-white font-medium">{match.sport}</div>
                              <div className="text-dark-800 text-sm">vs {match.opponent.name}</div>
                            </div>
                            <div className="flex items-center text-dark-800 text-sm">
                              <Calendar className="h-4 w-4 mr-1" />
                              <span>{formatDateForDisplay(match.date)}</span>
                              <Clock className="h-4 w-4 ml-3 mr-1" />
                              <span>{match.time}</span>
                            </div>
                          </div>
                          <div className="flex items-center text-dark-800 text-sm mt-2">
                            <MapPin className="h-4 w-4 mr-1" />
                            <span>{match.venue.name}, {match.venue.city}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberMatchesModal;